import type { StoreApi } from "zustand";

import type { AskPerformYardState } from "./types";

// Lazy store reference — set by store/index.ts after creation to avoid circular imports.
let storeRef: StoreApi<AskPerformYardState> | null = null;

export const setStoreRef = (store: StoreApi<AskPerformYardState>): void => {
	storeRef = store;
};

/**
 * Streaming chunk buffer — accumulates chunks and flushes once per animation
 * frame, so a fast stream doesn't trigger a full re-render per token.
 *
 * Keyed by message id rather than holding one pending slot (PY-13559). Turns in
 * two different conversations can now be in flight at once; a single slot let
 * the second stream's chunks land under the first stream's message id, splicing
 * one answer into the other.
 */
const pendingByMessageId = new Map<string, string>();
let rafHandle: number | null = null;

export const flushPendingChunks = (): void => {
	rafHandle = null;
	if (pendingByMessageId.size === 0) return;

	const flushing = new Map(pendingByMessageId);
	pendingByMessageId.clear();

	storeRef?.setState((state) => {
		// Only the message currently at the tail is on screen; anything else
		// belongs to a conversation the user has switched away from, and its text
		// is tracked separately as a pending turn.
		const lastIdx = state.messages.length - 1;
		const lastMsg = state.messages[lastIdx];
		if (!lastMsg) return state;

		const chunks = flushing.get(lastMsg.id);
		if (!chunks) return state;

		const updated = state.messages.slice();
		updated[lastIdx] = { ...lastMsg, content: lastMsg.content + chunks };
		return { messages: updated };
	});
};

/**
 * Take and clear whatever is buffered.
 *
 * With a `messageId`, drains only that message — used when one turn finishes
 * while another is still streaming. Without one, drains everything, which is
 * what a full chat reset wants.
 */
export const drainPendingChunks = (messageId?: string): string => {
	if (messageId === undefined) {
		if (rafHandle !== null) {
			cancelAnimationFrame(rafHandle);
			rafHandle = null;
		}
		pendingByMessageId.clear();
		return "";
	}

	const chunks = pendingByMessageId.get(messageId) ?? "";
	pendingByMessageId.delete(messageId);

	// Only stand the frame down once nothing else is waiting on it.
	if (pendingByMessageId.size === 0 && rafHandle !== null) {
		cancelAnimationFrame(rafHandle);
		rafHandle = null;
	}

	return chunks;
};

export const schedulePendingChunk = (messageId: string, chunk: string): void => {
	pendingByMessageId.set(messageId, (pendingByMessageId.get(messageId) ?? "") + chunk);
	if (rafHandle === null) {
		rafHandle = requestAnimationFrame(flushPendingChunks);
	}
};
